import {Location} from '@angular/common';
import {DestroyRef, EffectRef, Injectable, Signal, WritableSignal, computed, effect, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormGroup} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {catchError, debounceTime, defaultIfEmpty, finalize, forkJoin, Observable, of, Subscription, switchMap, take, tap, throwError, timer} from 'rxjs';
import {FeedbackAttemptFacade} from '../../../shared/services/feedback-attempt-facade.service';
import {AttemptFormValue} from '../../../shared/types/feedback-types/attemptFormValue.type';
import {FeedbackAttemptContext} from '../../../shared/types/feedback-types/feedbackAttemptContext.interface';
import {FeedbackAttemptNavigationTarget} from '../../../shared/types/feedback-types/feedbackAttemptNavigationTarget.interface';
import {
	FeedbackAnswerState,
	FeedbackAnswerStateResource,
	FeedbackAnswerUpdateRequest,
	FeedbackAnswerUpdateResource,
	FeedbackFormPresentationResource,
	FeedbackPresentationMode,
	FeedbackPresentationQuestionResource,
	FeedbackQuestionResource,
	FileStorageResource,
} from '../../../shared/types/generated';
import {FeedbackCompletionMode} from '../../../shared/types/generated/feedbackCompletionMode';
import {normalizeCompletionMode, normalizeCompletionText} from '../feedback-attempt-completion.utils';
import {
	FeedbackStructureQuestionFormQuestion,
	parseFeedbackQuestionResourceForForm,
} from '../feedback-structure-create-update-dialog/feedback-structure-question/feedback-structure-question-form.model';
import {activeQuestionIdsFromAnswerUpdate, formatCountdown, isCompletedAnswer} from '../helpers/feedback-attempt-context.utils';
import {feedbackAnswerControlName} from '../helpers/feedback-structure-preview-control-names';
import {
	createAnswerRequest,
	createLifecycleStateRequest,
	createOneByOneProgressionAnswerRequest,
	createTimeoutAnswerRequest,
} from './feedback-attempt-answer-mapper';
import {
	changedControlNames,
	controlValue,
	markAllControlsTouched,
	questionControlNames,
	setQuestionControlsDisabled,
	syncAttemptFormControls,
} from './feedback-attempt-form-controls';
import {
	createRequiredCommentValidator,
	getValidationErrorQuestionIds,
	hasMissingRequiredAnswer,
	hasQuestionAnswer,
	questionCanBeAutosaved,
	requiredCommentsFilled,
	updateAnswerValidationStates,
	updateCommentValidationStates,
} from './feedback-attempt-validation';

@Injectable()
export class FeedbackAttemptPageController {
	private route: ActivatedRoute = inject(ActivatedRoute);
	private router: Router = inject(Router);
	private location: Location = inject(Location);
	private destroyRef: DestroyRef = inject(DestroyRef);
	private attemptFacade: FeedbackAttemptFacade = inject(FeedbackAttemptFacade);

	public readonly controlNamePrefix: string = 'attempt';
	public readonly attemptForm: FormGroup = new FormGroup({});

	private missingContextSignal: WritableSignal<boolean> = signal(false);
	private displayAttachmentsDialogSignal: WritableSignal<boolean> = signal(false);
	private formControlsReadySignal: WritableSignal<boolean> = signal(false);
	private isSavingSignal: WritableSignal<boolean> = signal(false);
	private oneByOneStartedSignal: WritableSignal<boolean> = signal(false);
	private oneByOneQuestionIndexSignal: WritableSignal<number> = signal(0);
	private oneByOneRemainingSecondsSignal: WritableSignal<number | null> = signal(null);
	private oneByOneQuestionExpiredSignal: WritableSignal<boolean> = signal(false);
	private oneByOneDisplayedQuestionIdsOverride: WritableSignal<number[] | null> = signal(null);
	private formValidationVersion: WritableSignal<number> = signal(0);
	private currentAttemptId: WritableSignal<number | null> = signal(null);
	private timeoutFinalizingQuestionId: WritableSignal<number | null> = signal(null);
	private timeoutFinalizedQuestionId: WritableSignal<number | null> = signal(null);
	private timeoutFinalizedQuestionResult: WritableSignal<FeedbackAnswerUpdateResource | null> = signal(null);

	public readonly missingContext: Signal<boolean> = this.missingContextSignal.asReadonly();
	public readonly displayAttachmentsDialog: WritableSignal<boolean> = this.displayAttachmentsDialogSignal;
	public readonly formControlsReady: Signal<boolean> = this.formControlsReadySignal.asReadonly();
	public readonly isSaving: Signal<boolean> = this.isSavingSignal.asReadonly();
	public readonly oneByOneStarted: Signal<boolean> = this.oneByOneStartedSignal.asReadonly();
	public readonly oneByOneQuestionIndex: Signal<number> = this.oneByOneQuestionIndexSignal.asReadonly();
	public readonly oneByOneRemainingSeconds: Signal<number | null> = this.oneByOneRemainingSecondsSignal.asReadonly();
	public readonly oneByOneQuestionExpired: Signal<boolean> = this.oneByOneQuestionExpiredSignal.asReadonly();

	private activeTimedQuestionId: number | null = null;
	private submitted: boolean = false;
	private explicitSubmitInProgress: boolean = false;
	private destroyed: boolean = false;
	private autosaveSubscription: Subscription | null = null;
	private validationSubscription: Subscription | null = null;
	private lockRefreshSubscription: Subscription | null = null;
	private oneByOneTimerSubscription: Subscription | null = null;
	private lastAutosaveSnapshot: AttemptFormValue = {};
	private lastSyncedAnswersRef: FeedbackAnswerStateResource[] | null = null;
	private lastSyncedQuestionsRef: FeedbackStructureQuestionFormQuestion[] | null = null;
	private readonly presentedQuestionIds: Set<number> = new Set<number>();
	private readonly presentingQuestionIds: Set<number> = new Set<number>();
	private readonly requiredCommentValidator: ReturnType<typeof createRequiredCommentValidator> = createRequiredCommentValidator();
	private readonly getAttemptControlValue: (controlName: string) => unknown = (controlName: string): unknown =>
		controlValue(this.attemptForm, controlName);

	public readonly context: Signal<FeedbackAttemptContext | null> = this.attemptFacade.attemptContext;
	public readonly presentation: Signal<FeedbackFormPresentationResource | null> = computed(() => this.context()?.presentation ?? null);
	public readonly completionMode: Signal<FeedbackCompletionMode> = computed(() => normalizeCompletionMode(this.presentation()?.completion));
	public readonly hasVisibleAuthenticatedCompletion: Signal<boolean> = computed(() => {
		const completionState = this.context()?.completion;
		if (completionState === null || completionState === undefined) {
			return false;
		}

		if (this.completionMode() === FeedbackCompletionMode.DISABLED) {
			return false;
		}

		if (this.completionMode() === FeedbackCompletionMode.EVALUATION) {
			return true;
		}

		return normalizeCompletionText(this.presentation()?.completion?.text) !== null
			|| normalizeCompletionText(this.presentation()?.completion?.failed_text) !== null;
	});
	public readonly completionVisible: Signal<boolean> = computed(() =>
		!this.attemptFacade.isPublicAttempt() && this.hasVisibleAuthenticatedCompletion()
	);
	public readonly isOneByOne: Signal<boolean> = computed(() =>
		this.presentation()?.presentation_mode === FeedbackPresentationMode.ONE_BY_ONE
	);
	public readonly questions: Signal<FeedbackStructureQuestionFormQuestion[]> = computed(() =>
		(this.presentation()?.questions ?? []).map((question) => this.presentationQuestionToStructureQuestion(question))
	);
	public readonly displayedQuestions: Signal<FeedbackStructureQuestionFormQuestion[]> = computed(() => {
		const questions = this.questions();
		const activeQuestionIds = this.oneByOneDisplayedQuestionIdsOverride() ?? this.context()?.activeQuestionIds;

		if (activeQuestionIds === null || activeQuestionIds === undefined) {
			return questions;
		}

		if (activeQuestionIds.length === 0) {
			return [];
		}

		return questions.filter((question) => question.id !== undefined && activeQuestionIds.includes(question.id));
	});
	public readonly shouldShowOneByOneIntro: Signal<boolean> = computed(() => this.isOneByOne() && !this.oneByOneStarted());
	public readonly shouldShowQuestionForm: Signal<boolean> = computed(() =>
		this.completionVisible() === false
		&& (!this.isOneByOne() || this.oneByOneStarted() || this.displayedQuestions().length === 0)
	);
	public readonly activeOneByOneQuestionIndex: Signal<number> = computed(() => {
		const lastQuestionIndex = this.displayedQuestions().length - 1;
		return lastQuestionIndex < 0 ? 0 : Math.min(this.oneByOneQuestionIndex(), lastQuestionIndex);
	});
	public readonly activeOneByOneQuestion: Signal<FeedbackStructureQuestionFormQuestion | null> = computed(() => {
		if (!this.isOneByOne() || !this.oneByOneStarted()) {
			return null;
		}

		return this.displayedQuestions()[this.activeOneByOneQuestionIndex()] ?? null;
	});
	public readonly activeOneByOneQuestionNumber: Signal<number> = computed(() =>
		this.activeOneByOneQuestion() === null ? 0 : this.activeOneByOneQuestionIndex() + 1
	);
	public readonly hasActiveOneByOneTimer: Signal<boolean> = computed(() =>
		this.activeOneByOneQuestion()?.time_limit_seconds !== null
	);
	public readonly activeOneByOneQuestionLocked: Signal<boolean> = computed(() =>
		this.isOneByOne() && this.oneByOneStarted() && this.oneByOneQuestionExpired()
	);
	public readonly activeOneByOneTimerText: Signal<string> = computed(() => formatCountdown(this.oneByOneRemainingSeconds()));
	public readonly isLastOneByOneQuestion: Signal<boolean> = computed(() => {
		if (!this.isOneByOne()) {
			return true;
		}

		return this.displayedQuestions().length <= 1
			|| this.activeOneByOneQuestionIndex() >= this.displayedQuestions().length - 1;
	});
	public readonly canSubmit: Signal<boolean> = computed(() => {
		const attemptId = this.activeAttemptId();
		if (attemptId === null || this.isSaving() || this.context()?.saving === true) {
			return false;
		}

		const attemptIdKey = String(attemptId);
		return Boolean(this.attemptFacade.attemptLockTokens()[attemptIdKey])
			&& this.attemptFacade.attemptReadonlyLocks()[attemptIdKey] !== true;
	});
	public readonly canFinishAttempt: Signal<boolean> = computed(() => {
		this.formValidationVersion();
		return this.canSubmit() && !this.isOneByOne() && this.getCurrentValidationErrorQuestionIds().length === 0;
	});
	public readonly canAdvanceOneByOne: Signal<boolean> = computed(() => {
		this.formValidationVersion();
		if (!this.canSubmit() || !this.isOneByOne()) {
			return false;
		}

		const activeQuestion = this.activeOneByOneQuestion();
		return activeQuestion?.id !== undefined
			&& !this.getCurrentValidationErrorQuestionIds().includes(activeQuestion.id);
	});

	private formControlSync: EffectRef = effect(() => {
		const questions = this.questions();
		const answers = this.context()?.attempt.answers ?? [];
		if (this.lastSyncedQuestionsRef === questions && this.lastSyncedAnswersRef === answers) {
			this.updateFormValidationState();
			return;
		}

		syncAttemptFormControls(
			this.attemptForm,
			questions,
			answers,
			this.controlNamePrefix,
			(questionId) => this.isQuestionLocked(questionId)
		);
		this.lastSyncedQuestionsRef = questions;
		this.lastSyncedAnswersRef = answers;
		this.updateFormValidationState();
	});
	private questionAnswerAsPresentedMarker: EffectRef = effect(() => this.markActiveOneByOneQuestionAsPresented());
	private oneByOneTimerEffect: EffectRef = effect(() => this.syncActiveOneByOneTimer());
	private visibleValidationEffect: EffectRef = effect(() => this.syncVisibleValidationState());

	public constructor() {
		this.destroyRef.onDestroy(() => this.destroy());
	}

	public initialize(): void {
		const instanceId = this.routeInstanceId();
		if (instanceId === null) {
			this.navigateBack();
			return;
		}

		this.startAttempt(instanceId);
	}

	public handleBeforeUnload(): void {
		const attemptId = this.activeAttemptId();
		if (attemptId === null || this.submitted) {
			return;
		}

		this.attemptFacade.unlockAttemptOnUnload(attemptId)
			.pipe(take(1))
			.subscribe();
	}

	public navigateBack(): void {
		this.navigateToTarget(this.attemptFacade.getBackNavigationTarget());
	}

	public displayClass(): string {
		switch (this.presentation()?.presentation_mode) {
			case FeedbackPresentationMode.ONE_BY_ONE:
				return 'one-at-atime';
			case FeedbackPresentationMode.CARDS:
				return 'multicard-display';
			case FeedbackPresentationMode.SINGLE_FORM:
			default:
				return 'oneform-display';
		}
	}

	public startOneByOneFlow(): void {
		if (!this.isOneByOne()) {
			return;
		}

		this.oneByOneStartedSignal.set(true);
		this.focusActiveOneByOneQuestion();
	}

	public showNextQuestion(): void {
		const attemptId = this.activeAttemptId();
		const question = this.activeOneByOneQuestionWithId();
		if (!this.isOneByOne() || attemptId === null || question === null || this.isSaving() || !this.canAdvanceOneByOne()) {
			return;
		}

		const cachedTimeoutResult = this.timeoutFinalizedResultForQuestion(question.id);
		if (cachedTimeoutResult !== null) {
			this.handleOneByOneProgressionResult(attemptId, cachedTimeoutResult);
			return;
		}

		if (this.isQuestionCompleted(question.id)) {
			this.advanceOneByOneFromCurrentContext(attemptId);
			return;
		}

		this.isSavingSignal.set(true);
		this.finalizeOneByOneQuestion(attemptId, question).pipe(
			take(1),
			finalize(() => this.isSavingSignal.set(false))
		).subscribe({
			next: (result) => {
				if (result === null) {
					return;
				}

				this.handleOneByOneProgressionResult(attemptId, result);
			},
		});
	}

	public submitAttempt(): void {
		const attemptId = this.activeAttemptId();
		if (attemptId === null || !this.canSubmit()) {
			return;
		}

		if (this.isOneByOne()) {
			if (!this.canAdvanceOneByOne()) {
				return;
			}

			this.submitOneByOneAttempt(attemptId);
			return;
		}

		const validationErrorQuestionIds = this.getCurrentValidationErrorQuestionIds();
		this.attemptFacade.setAttemptValidationErrors(validationErrorQuestionIds);
		if (validationErrorQuestionIds.length > 0) {
			markAllControlsTouched(this.attemptForm);
			return;
		}

		this.explicitSubmitInProgress = true;
		this.isSavingSignal.set(true);
		this.saveDisplayedAnswers(attemptId, false).pipe(
			switchMap(() => this.attemptFacade.submitAttempt(attemptId, false)),
			take(1),
			finalize(() => {
				this.explicitSubmitInProgress = false;
				this.isSavingSignal.set(false);
			})
		).subscribe({
			next: () => {
				this.handleSuccessfulSubmission(attemptId);
			},
		});
	}

	public uploadQuestionFiles(
		question: FeedbackStructureQuestionFormQuestion & {id: number},
		documents: FileList,
		currentFiles: FileStorageResource[]
	): Observable<FileStorageResource[]> {
		const attemptId = this.activeAttemptId();
		if (attemptId === null || documents.length === 0) {
			return of(currentFiles);
		}

		return forkJoin(
			Array.from(documents).map((document) => this.attemptFacade.uploadAttemptAnswerFile(attemptId, question.id, document))
		).pipe(
			switchMap((uploadedFiles) => this.syncQuestionFiles(attemptId, question, [...uploadedFiles, ...currentFiles]))
		);
	}

	public removeQuestionFile(
		question: FeedbackStructureQuestionFormQuestion & {id: number},
		fileToRemove: FileStorageResource,
		currentFiles: FileStorageResource[]
	): Observable<FileStorageResource[]> {
		const attemptId = this.activeAttemptId();
		const nextFiles = this.removeQuestionFileFromList(currentFiles, fileToRemove);
		if (attemptId === null || fileToRemove.uuid === undefined || nextFiles.length === currentFiles.length) {
			return of(currentFiles);
		}

		return this.attemptFacade.detachAttemptAnswerFile(attemptId, question.id, fileToRemove.uuid).pipe(
			switchMap(() => this.syncQuestionFiles(attemptId, question, nextFiles))
		);
	}

	public hasValidationError(question: FeedbackStructureQuestionFormQuestion): boolean {
		return question.id !== undefined && (this.context()?.validationErrorQuestionIds ?? []).includes(question.id);
	}

	public hasRequiredAnswerError(question: FeedbackStructureQuestionFormQuestion): boolean {
		return hasMissingRequiredAnswer(
			question,
			this.controlNamePrefix,
			this.getAttemptControlValue,
			(questionId) => this.shouldSkipOneByOneValidation(questionId),
			(questionId) => this.isQuestionLocked(questionId)
		);
	}

	private startAttempt(instanceId: number): void {
		this.missingContextSignal.set(false);
		this.formControlsReadySignal.set(false);

		this.attemptFacade.enterAttempt(instanceId).pipe(take(1)).subscribe({
			next: (attempt) => {
				const formId = attempt.instance.form.id;
				this.location.replaceState(this.attemptFacade.getAttemptBrowserUrl(attempt.instance.id));
				this.initializeAttempt(attempt.id, formId);
			},
			error: () => {
				this.missingContextSignal.set(true);
				this.navigateBack();
			},
		});
	}

	private initializeAttempt(attemptId: number, formId: number): void {
		this.currentAttemptId.set(attemptId);
		this.formControlsReadySignal.set(false);
		this.oneByOneStartedSignal.set(false);
		this.oneByOneQuestionIndexSignal.set(0);
		this.oneByOneDisplayedQuestionIdsOverride.set(null);
		this.resetOneByOneQuestionState();
		this.presentedQuestionIds.clear();
		this.presentingQuestionIds.clear();
		this.attemptFacade.loadAttemptPresentation(formId).pipe(take(1)).subscribe({
			next: () => {
				const answers = this.context()?.attempt.answers ?? [];
				syncAttemptFormControls(
					this.attemptForm,
					this.questions(),
					answers,
					this.controlNamePrefix,
					(questionId) => this.isQuestionLocked(questionId)
				);
				this.initializePresentedOneByOneQuestions(answers);
				this.initializeOneByOneResumeState();
				this.setupAnswerAutosave();
				this.formControlsReadySignal.set(true);
			},
			error: () => undefined,
		});

		this.lockRefreshSubscription?.unsubscribe();
		this.lockRefreshSubscription = timer(0, 30000).pipe(
			switchMap(() => this.attemptFacade.lockAttempt(attemptId).pipe(catchError(() => of(null)))),
			takeUntilDestroyed(this.destroyRef)
		).subscribe();
	}

	private destroy(): void {
		if (this.destroyed) {
			return;
		}

		this.destroyed = true;
		this.teardownAnswerAutosave();
		this.stopLockRefresh();
		this.stopOneByOneTimer();
		const attemptId = this.activeAttemptId();
		if (attemptId === null || this.submitted) {
			return;
		}

		this.attemptFacade.unlockAttempt(attemptId).pipe(
			take(1),
			catchError(() => of(void 0)),
			take(1)
		).subscribe();
	}

	private saveDisplayedAnswers(attemptId: number, ignoreErrors: boolean): Observable<(FeedbackAnswerUpdateResource | null)[]> {
		const updateRequests = this.displayedQuestions()
			.filter((question): question is FeedbackStructureQuestionFormQuestion & {id: number} => question.id !== undefined)
			.map((question) => ({
				question,
				answerRequest: createAnswerRequest(question, this.controlNamePrefix, this.getAttemptControlValue),
			}))
			.filter(({question, answerRequest}) =>
				answerRequest.state !== FeedbackAnswerState.ANSWERED
				|| requiredCommentsFilled(question, this.controlNamePrefix, this.getAttemptControlValue)
			)
			.map(({question, answerRequest}) => this.updateAttemptAnswerRequestWithLock(attemptId, question, answerRequest, ignoreErrors));

		return updateRequests.length > 0 ? forkJoin(updateRequests) : of([]);
	}

	private setupAnswerAutosave(): void {
		this.teardownAnswerAutosave();
		this.setupValidationTracking();
		this.lastAutosaveSnapshot = this.attemptForm.getRawValue() as AttemptFormValue;
		if (this.isOneByOne()) {
			return;
		}

		this.autosaveSubscription = this.attemptForm.valueChanges.pipe(
			tap(() => this.updateFormValidationState()),
			debounceTime(250),
			takeUntilDestroyed(this.destroyRef)
		).subscribe(() => this.autosaveChangedAnswers());
	}

	private teardownAnswerAutosave(): void {
		this.autosaveSubscription?.unsubscribe();
		this.autosaveSubscription = null;
		this.validationSubscription?.unsubscribe();
		this.validationSubscription = null;
	}

	private setupValidationTracking(): void {
		this.validationSubscription?.unsubscribe();
		this.validationSubscription = this.attemptForm.valueChanges.pipe(
			tap(() => this.updateFormValidationState()),
			takeUntilDestroyed(this.destroyRef)
		).subscribe();
	}

	private autosaveChangedAnswers(): void {
		const attemptId = this.activeAttemptId();
		if (attemptId === null || this.submitted || this.context()?.saving === true) {
			return;
		}

		this.updateFormValidationState();
		const currentSnapshot = this.attemptForm.getRawValue() as AttemptFormValue;
		const changedNames = changedControlNames(this.lastAutosaveSnapshot, currentSnapshot);
		this.lastAutosaveSnapshot = currentSnapshot;
		const changedQuestions = this.displayedQuestions()
			.filter((question): question is FeedbackStructureQuestionFormQuestion & {id: number} =>
				question.id !== undefined
				&& questionControlNames(question, this.controlNamePrefix).some((controlName) => changedNames.has(controlName))
				&& questionCanBeAutosaved(question, this.controlNamePrefix, this.getAttemptControlValue)
			);

		changedQuestions.forEach((question) => {
			this.updateAttemptAnswerWithLock(attemptId, question);
		});
	}

	private updateAttemptAnswerWithLock(
		attemptId: number,
		question: FeedbackStructureQuestionFormQuestion & {id: number}
	): void {
		this.updateAttemptAnswerRequestWithLock(
			attemptId,
			question,
			createAnswerRequest(question, this.controlNamePrefix, this.getAttemptControlValue),
			true
		).subscribe();
	}

	private markActiveOneByOneQuestionAsPresented(): void {
		const attemptId = this.activeAttemptId();
		const question = this.activeOneByOneQuestionWithId();
		if (!this.formControlsReady() || !this.isOneByOne() || attemptId === null || question === null) {
			return;
		}

		if (this.presentedQuestionIds.has(question.id) || this.presentingQuestionIds.has(question.id)) {
			return;
		}

		this.presentingQuestionIds.add(question.id);
		this.updateAttemptAnswerRequestWithLock(
			attemptId,
			question,
			createLifecycleStateRequest(FeedbackAnswerState.PRESENTED),
			true
		).pipe(
			take(1),
			finalize(() => this.presentingQuestionIds.delete(question.id))
		).subscribe((result) => {
			if (result !== null) {
				this.presentedQuestionIds.add(question.id);
			}
		});
	}

	private updateAttemptAnswerRequestWithLock(
		attemptId: number,
		question: FeedbackStructureQuestionFormQuestion & {id: number},
		answerRequest: FeedbackAnswerUpdateRequest,
		ignoreErrors: boolean
	): Observable<FeedbackAnswerUpdateResource | null> {
		const attemptIdKey = String(attemptId);
		const hasLockToken = Boolean(this.attemptFacade.attemptLockTokens()[attemptIdKey]);
		const updateAnswer = () => this.attemptFacade.updateAttemptAnswer(attemptId, question.id, answerRequest)
			.pipe(take(1), defaultIfEmpty(null));
		const request = hasLockToken
			? updateAnswer()
			: this.attemptFacade.lockAttempt(attemptId).pipe(
				take(1),
				switchMap((lockToken) => lockToken ? updateAnswer() : of(null))
			);
		const handledRequest = request.pipe(
			tap((result) => {
				if (result?.end_feedback && this.isOneByOne()) {
					this.autoSubmitAttempt(attemptId);
				}
			})
		);

		return ignoreErrors ? handledRequest.pipe(catchError(() => of(null))) : handledRequest;
	}

	private syncQuestionFiles(
		attemptId: number,
		question: FeedbackStructureQuestionFormQuestion & {id: number},
		nextFiles: FileStorageResource[]
	): Observable<FileStorageResource[]> {
		return this.updateAttemptAnswerRequestWithLock(
			attemptId,
			question,
			this.createAnswerRequestWithFiles(question, nextFiles),
			false
		).pipe(
			switchMap((result) => result === null
				? throwError(() => new Error('Failed to synchronize feedback attempt files.'))
				: of(nextFiles))
		);
	}

	private finalizeOneByOneQuestion(
		attemptId: number,
		question: FeedbackStructureQuestionFormQuestion & {id: number}
	): Observable<FeedbackAnswerUpdateResource | null> {
		return this.updateAttemptAnswerRequestWithLock(
			attemptId,
			question,
			createOneByOneProgressionAnswerRequest(
				question,
				this.controlNamePrefix,
				this.getAttemptControlValue,
				questionCanBeAutosaved(question, this.controlNamePrefix, this.getAttemptControlValue)
			),
			false
		);
	}

	private submitOneByOneAttempt(attemptId: number): void {
		const question = this.activeOneByOneQuestionWithId();
		this.attemptFacade.setAttemptValidationErrors([]);
		this.explicitSubmitInProgress = true;
		this.isSavingSignal.set(true);

		const finalizeQuestion = question === null
			? of(null)
			: this.resolveOneByOneSubmitFinalization(attemptId, question);

		finalizeQuestion.pipe(
			switchMap(() => this.attemptFacade.submitAttempt(attemptId, false)),
			take(1),
			finalize(() => {
				this.explicitSubmitInProgress = false;
				this.isSavingSignal.set(false);
			})
		).subscribe({
			next: () => {
				this.handleSuccessfulSubmission(attemptId);
			},
		});
	}

	private autoSubmitAttempt(attemptId: number): void {
		if (this.submitted || this.explicitSubmitInProgress) {
			return;
		}

		this.explicitSubmitInProgress = true;
		this.isSavingSignal.set(true);
		this.attemptFacade.submitAttempt(attemptId, true).pipe(
			take(1),
			finalize(() => {
				this.explicitSubmitInProgress = false;
				this.isSavingSignal.set(false);
			})
		).subscribe({
			next: () => {
				this.handleSuccessfulSubmission(attemptId);
			},
		});
	}

	private handleSuccessfulSubmission(attemptId: number): void {
		this.submitted = true;
		this.stopLockRefresh();

		if (this.attemptFacade.isPublicAttempt()) {
			this.navigateToTarget(this.attemptFacade.getCompletionNavigationTarget(this.context()?.instance.id ?? attemptId));
			return;
		}

		if (!this.completionVisible()) {
			this.navigateBack();
		}
	}

	private navigateToTarget(target: FeedbackAttemptNavigationTarget): void {
		void this.router.navigate(target.commands, target.queryParams ? {queryParams: target.queryParams} : undefined);
	}

	private initializeOneByOneResumeState(): void {
		if (!this.isOneByOne()) {
			return;
		}

		const nextQuestionId = this.context()?.attempt.next_question_id;
		if (nextQuestionId !== null && nextQuestionId !== undefined) {
			this.showOneByOneQuestionById(nextQuestionId);
			return;
		}

		const firstUnansweredQuestionIndex = this.firstUnansweredOneByOneQuestionIndex();
		if (firstUnansweredQuestionIndex !== null) {
			this.oneByOneQuestionIndexSignal.set(firstUnansweredQuestionIndex);
		}
	}

	private updateFormValidationState(): void {
		const questionsToValidate = this.validationQuestions();
		const validationErrorQuestionIds = this.getCurrentValidationErrorQuestionIds();
		updateAnswerValidationStates(
			this.attemptForm,
			questionsToValidate,
			this.controlNamePrefix
		);
		updateCommentValidationStates(
			this.attemptForm,
			questionsToValidate,
			this.controlNamePrefix,
			this.requiredCommentValidator,
			this.getAttemptControlValue
		);
		if (!this.areQuestionIdsEqual(this.context()?.validationErrorQuestionIds ?? [], validationErrorQuestionIds)) {
			this.attemptFacade.setAttemptValidationErrors(validationErrorQuestionIds);
		}
		this.bumpFormValidationVersion();
	}

	private bumpFormValidationVersion(): void {
		this.formValidationVersion.update((version) => version + 1);
	}

	private stopLockRefresh(): void {
		this.lockRefreshSubscription?.unsubscribe();
		this.lockRefreshSubscription = null;
	}

	private getCurrentValidationErrorQuestionIds(): number[] {
		return getValidationErrorQuestionIds(
			this.validationQuestions(),
			this.controlNamePrefix,
			this.getAttemptControlValue,
			(questionId) => this.shouldSkipOneByOneValidation(questionId),
			(questionId) => this.isQuestionLocked(questionId)
		);
	}

	private validationQuestions(): FeedbackStructureQuestionFormQuestion[] {
		if (!this.formControlsReady() || this.completionVisible() || !this.shouldShowQuestionForm()) {
			return [];
		}

		if (!this.isOneByOne()) {
			return this.displayedQuestions();
		}

		const activeQuestion = this.activeOneByOneQuestion();
		return activeQuestion === null ? [] : [activeQuestion];
	}

	private syncVisibleValidationState(): void {
		this.formControlsReady();
		this.completionVisible();
		this.shouldShowQuestionForm();
		this.isOneByOne();
		this.oneByOneStarted();
		this.displayedQuestions();
		this.activeOneByOneQuestion();
		this.updateFormValidationState();
	}

	private areQuestionIdsEqual(firstIds: number[], secondIds: number[]): boolean {
		return firstIds.length === secondIds.length
			&& firstIds.every((questionId, index) => questionId === secondIds[index]);
	}

	private isQuestionCompleted(questionId: number): boolean {
		const answer = this.context()?.attempt.answers.find((currentAnswer) => currentAnswer.question_id === questionId);
		return isCompletedAnswer(answer);
	}

	private activeOneByOneQuestionWithId(): (FeedbackStructureQuestionFormQuestion & {id: number}) | null {
		const question = this.activeOneByOneQuestion();
		return question?.id === undefined ? null : question as FeedbackStructureQuestionFormQuestion & {id: number};
	}

	private showOneByOneQuestionById(questionId: number): void {
		const nextQuestionIndex = this.displayedQuestions().findIndex((question) => question.id === questionId);
		if (nextQuestionIndex >= 0) {
			this.oneByOneQuestionIndexSignal.set(nextQuestionIndex);
		}
	}

	private firstUnansweredOneByOneQuestionIndex(): number | null {
		const completedQuestionIds = new Set(
			(this.context()?.attempt.answers ?? [])
				.filter((answer) => isCompletedAnswer(answer))
				.map((answer) => answer.question_id)
		);
		const unansweredQuestionIndex = this.displayedQuestions()
			.findIndex((question) => question.id !== undefined && !completedQuestionIds.has(question.id));

		if (unansweredQuestionIndex >= 0) {
			return unansweredQuestionIndex;
		}

		return this.displayedQuestions().length > 0 ? this.displayedQuestions().length - 1 : null;
	}

	private initializePresentedOneByOneQuestions(answers: FeedbackAnswerStateResource[]): void {
		if (!this.isOneByOne()) {
			return;
		}

		answers
			.filter((answer) => answer.state === FeedbackAnswerState.PRESENTED)
			.forEach((answer) => this.presentedQuestionIds.add(answer.question_id));
	}

	private activeAttemptId(): number | null {
		return this.currentAttemptId();
	}

	private routeInstanceId(): number | null {
		const rawInstanceId = this.route.snapshot.paramMap.get('instanceID');
		if (rawInstanceId === null) {
			return null;
		}

		const instanceId = Number(rawInstanceId);
		return Number.isFinite(instanceId) ? instanceId : null;
	}

	private presentationQuestionToStructureQuestion(
		question: FeedbackPresentationQuestionResource
	): FeedbackStructureQuestionFormQuestion {
		return parseFeedbackQuestionResourceForForm({
			...question,
			files: this.presentationQuestionFiles(question),
		} as FeedbackQuestionResource);
	}

	private presentationQuestionFiles(question: FeedbackPresentationQuestionResource): FileStorageResource[] {
		const files = (question as FeedbackPresentationQuestionResource & {files?: FileStorageResource[]}).files;
		return Array.isArray(files) ? files : [];
	}

	private createAnswerRequestWithFiles(
		question: FeedbackStructureQuestionFormQuestion,
		files: FileStorageResource[]
	): FeedbackAnswerUpdateRequest {
		const answerControlName = feedbackAnswerControlName(question, this.controlNamePrefix);
		return createAnswerRequest(
			question,
			this.controlNamePrefix,
			(controlName) => controlName === answerControlName ? files : this.getAttemptControlValue(controlName)
		);
	}

	private removeQuestionFileFromList(
		currentFiles: FileStorageResource[],
		fileToRemove: FileStorageResource
	): FileStorageResource[] {
		const nextFiles = [...currentFiles];
		const fileIndex = fileToRemove.uuid === undefined
			? nextFiles.indexOf(fileToRemove)
			: nextFiles.findIndex((file) => file.uuid === fileToRemove.uuid);

		if (fileIndex < 0) {
			return currentFiles;
		}

		nextFiles.splice(fileIndex, 1);
		return nextFiles;
	}

	private syncActiveOneByOneTimer(): void {
		const question = this.activeOneByOneQuestionWithId();

		if (!this.formControlsReady() || !this.isOneByOne() || !this.oneByOneStarted() || question === null) {
			this.activeTimedQuestionId = null;
			this.stopOneByOneTimer();
			this.oneByOneQuestionExpiredSignal.set(false);
			return;
		}

		if (this.activeTimedQuestionId !== question.id) {
			const previousQuestionId = this.activeTimedQuestionId;
			this.activeTimedQuestionId = question.id;
			this.resetOneByOneQuestionState(previousQuestionId);
		}

		if (question.time_limit_seconds === null) {
			this.stopOneByOneTimer();
			this.oneByOneRemainingSecondsSignal.set(null);
			this.oneByOneQuestionExpiredSignal.set(false);
			return;
		}

		const answer = this.answerForQuestion(question.id);
		const presentedAt = answer?.presented_at;
		if (presentedAt === null || presentedAt === undefined) {
			this.stopOneByOneTimer();
			this.oneByOneRemainingSecondsSignal.set(question.time_limit_seconds);
			this.oneByOneQuestionExpiredSignal.set(false);
			return;
		}

		const deadline = Date.parse(presentedAt) + (question.time_limit_seconds * 1000);
		if (Number.isNaN(deadline)) {
			this.stopOneByOneTimer();
			this.oneByOneRemainingSecondsSignal.set(question.time_limit_seconds);
			this.oneByOneQuestionExpiredSignal.set(false);
			return;
		}

		this.startOneByOneTimer(question, deadline);
	}

	private startOneByOneTimer(
		question: FeedbackStructureQuestionFormQuestion & {id: number},
		deadline: number
	): void {
		this.stopOneByOneTimer();
		this.updateOneByOneRemainingTime(question, deadline);
		if (this.oneByOneQuestionExpired()) {
			return;
		}

		this.oneByOneTimerSubscription = timer(1000, 1000).pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe(() => this.updateOneByOneRemainingTime(question, deadline));
	}

	private updateOneByOneRemainingTime(
		question: FeedbackStructureQuestionFormQuestion & {id: number},
		deadline: number
	): void {
		const remainingSeconds = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
		this.oneByOneRemainingSecondsSignal.set(remainingSeconds);

		if (remainingSeconds > 0) {
			this.oneByOneQuestionExpiredSignal.set(false);
			return;
		}

		this.stopOneByOneTimer();
		this.oneByOneQuestionExpiredSignal.set(true);
		setQuestionControlsDisabled(this.attemptForm, question, this.controlNamePrefix, true);

		if (this.timeoutFinalizingQuestionId() === question.id || this.timeoutFinalizedQuestionId() === question.id) {
			return;
		}

		const answer = this.answerForQuestion(question.id);
		if (answer !== undefined && this.isQuestionCompleted(question.id)) {
			if (answer.state === FeedbackAnswerState.TIMED_OUT || answer.state === FeedbackAnswerState.ANSWERED) {
				this.timeoutFinalizedQuestionId.set(question.id);
			}

			if (question.auto_advance_on_timeout) {
				this.advanceOneByOneFromCurrentContext(this.activeAttemptId());
			} else {
				this.focusOneByOneActionButton();
			}

			return;
		}

		this.handleActiveOneByOneQuestionTimeout(question);
	}

	private handleActiveOneByOneQuestionTimeout(question: FeedbackStructureQuestionFormQuestion & {id: number}): void {
		const attemptId = this.activeAttemptId();
		if (attemptId === null || this.submitted) {
			return;
		}

		this.timeoutFinalizingQuestionId.set(question.id);
		if (!question.auto_advance_on_timeout) {
			this.oneByOneDisplayedQuestionIdsOverride.set(
				this.displayedQuestions()
					.map((displayedQuestion) => displayedQuestion.id)
					.filter((questionId): questionId is number => questionId !== undefined)
			);
		}

		this.updateAttemptAnswerRequestWithLock(
			attemptId,
			question,
			createTimeoutAnswerRequest(
				question,
				this.controlNamePrefix,
				this.getAttemptControlValue,
				hasQuestionAnswer(question, this.controlNamePrefix, this.getAttemptControlValue)
			),
			false
		).pipe(
			take(1),
			finalize(() => this.timeoutFinalizingQuestionId.set(null))
		).subscribe({
			next: (result) => {
				if (result === null) {
					return;
				}

				this.timeoutFinalizedQuestionId.set(question.id);
				this.timeoutFinalizedQuestionResult.set(result);
				if (question.auto_advance_on_timeout) {
					this.handleOneByOneProgressionResult(attemptId, result);
					return;
				}

				this.focusOneByOneActionButton();
			},
		});
	}

	private resolveOneByOneSubmitFinalization(
		attemptId: number,
		question: FeedbackStructureQuestionFormQuestion & {id: number}
	): Observable<FeedbackAnswerUpdateResource | null> {
		const cachedTimeoutResult = this.timeoutFinalizedResultForQuestion(question.id);
		if (cachedTimeoutResult !== null || this.isQuestionCompleted(question.id)) {
			return of(cachedTimeoutResult);
		}

		return this.finalizeOneByOneQuestion(attemptId, question);
	}

	private handleOneByOneProgressionResult(attemptId: number, result: FeedbackAnswerUpdateResource): void {
		this.oneByOneDisplayedQuestionIdsOverride.set(null);
		this.resetOneByOneQuestionState();

		if (result.end_feedback) {
			this.autoSubmitAttempt(attemptId);
			return;
		}

		if (result.next_question_id !== null) {
			this.showOneByOneQuestionById(result.next_question_id);
			this.focusActiveOneByOneQuestion();
			return;
		}

		if ((activeQuestionIdsFromAnswerUpdate(result) ?? []).length === 0) {
			this.autoSubmitAttempt(attemptId);
			return;
		}

		const lastQuestionIndex = this.displayedQuestions().length - 1;
		if (lastQuestionIndex <= 0) {
			return;
		}

		this.oneByOneQuestionIndexSignal.update((questionIndex) => Math.min(questionIndex + 1, lastQuestionIndex));
		this.focusActiveOneByOneQuestion();
	}

	private advanceOneByOneFromCurrentContext(attemptId: number | null): void {
		if (attemptId === null) {
			return;
		}

		this.oneByOneDisplayedQuestionIdsOverride.set(null);
		const nextQuestionId = this.context()?.attempt.next_question_id ?? null;
		if (nextQuestionId !== null) {
			this.resetOneByOneQuestionState();
			this.showOneByOneQuestionById(nextQuestionId);
			this.focusActiveOneByOneQuestion();
			return;
		}

		const activeQuestionIds = this.context()?.attempt.active_question_ids ?? [];
		if (activeQuestionIds.length === 0) {
			this.autoSubmitAttempt(attemptId);
			return;
		}

		this.resetOneByOneQuestionState();
		const lastQuestionIndex = this.displayedQuestions().length - 1;
		this.oneByOneQuestionIndexSignal.update((questionIndex) => Math.min(questionIndex + 1, lastQuestionIndex));
		this.focusActiveOneByOneQuestion();
	}

	private answerForQuestion(questionId: number): FeedbackAnswerStateResource | undefined {
		return this.context()?.attempt.answers.find((answer) => answer.question_id === questionId);
	}

	private timeoutFinalizedResultForQuestion(questionId: number): FeedbackAnswerUpdateResource | null {
		return this.timeoutFinalizedQuestionId() === questionId ? this.timeoutFinalizedQuestionResult() : null;
	}

	private isQuestionLocked(questionId: number | undefined): boolean {
		return questionId !== undefined
			&& this.activeOneByOneQuestion()?.id === questionId
			&& this.activeOneByOneQuestionLocked();
	}

	private shouldSkipOneByOneValidation(questionId: number | undefined): boolean {
		if (!this.isOneByOne() || questionId === undefined) {
			return false;
		}

		return this.activeOneByOneQuestion()?.id !== questionId && this.isQuestionCompleted(questionId);
	}

	private resetOneByOneQuestionState(previousQuestionId: number | null = this.activeTimedQuestionId): void {
		const previousQuestion = this.questions().find((question) => question.id === previousQuestionId);
		if (previousQuestion !== undefined) {
			setQuestionControlsDisabled(this.attemptForm, previousQuestion, this.controlNamePrefix, false);
		}

		this.stopOneByOneTimer();
		this.oneByOneRemainingSecondsSignal.set(null);
		this.oneByOneQuestionExpiredSignal.set(false);
		this.timeoutFinalizingQuestionId.set(null);
		this.timeoutFinalizedQuestionId.set(null);
		this.timeoutFinalizedQuestionResult.set(null);
	}

	private stopOneByOneTimer(): void {
		this.oneByOneTimerSubscription?.unsubscribe();
		this.oneByOneTimerSubscription = null;
	}

	private focusOneByOneActionButton(): void {
		setTimeout(() => {
			const actionButton = document.querySelector('#feedback-attempt-one-by-one-action button:not([disabled]), #feedback-attempt-one-by-one-action:not([disabled])') as HTMLElement | null;
			actionButton?.focus();
		});
	}

	private focusActiveOneByOneQuestion(): void {
		setTimeout(() => {
			const questionElement = document.querySelector('#feedback-attempt-active-one-by-one-question') as HTMLElement | null;
			questionElement?.focus();
		});
	}
}
